import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Minus, Plus } from "lucide-react";

// ---------- DATA ----------
const tiers = [
  {
    id: "essentials",
    eyebrow: "Start here",
    name: "Essentials",
    price: "$99",
    cadence: "/ month",
    duration: "Introductory rate, goes to $149/month",
    headline: "The strategic starter kit, curated colleges, scholarships, and a story-driven resume.",
    description:
      "Built for sophomores and juniors who want a head start without a full-program commitment. You'll get the same Scholar Mosaic research desk that powers our top-tier work, delivered in calm monthly batches.",
    features: [
      "College List Building (15+ schools, HBCU Framework™)",
      "Monthly Scholarship Spreadsheets (6 months)",
      "Fellowship & Summer Program Matches (3 opportunities)",
      "College Resume, 1 strategic version",
      "Application Planning + 1 essay structural feedback",
    ],
    note: "No commitment. Upgrade to Phase 1 anytime.",
    cta: "Start Essentials",
    style: "light",
    badge: null,
    showFounding: false,
    testId: "service-tier-1",
  },
  {
    id: "phase1",
    eyebrow: "For the family ready to win",
    name: "Phase 1: College Planning Sprint",
    price: "$4,800",
    cadence: "/ 3, 4 months",
    duration: "College Planning Sprint, limited spots",
    headline: "Everything you need before applications open, executed in one focused intensive.",
    description:
      "The intensive foundation. Three to four months of hands-on college planning, list, resume, scholarships, benefits navigation, everything you need before applications open. Founding client rate. Limited to 5 families.",
    features: [
      "College list (15 schools, HBCU Framework™)",
      "10+ fellowship & summer program matches",
      "Benefits navigation (SSI / SSDI / ABLE / Voc Rehab)",
      "College resume (2 strategic versions)",
      "Scholarship tracker + monthly lists (3 months)",
      "Application planning overview",
      "6 coaching calls",
    ],
    note: "Extends to Phase 2 (applications) and Phase 3 (transition) as needed.",
    cta: "Apply for Phase 1",
    style: "dark",
    badge: "Founding client rate",
    showFounding: true,
    testId: "service-tier-2",
  },
  {
    id: "full-journey",
    eyebrow: "The full Scholar Mosaic",
    name: "Full Journey",
    price: "$8,500",
    cadence: "/ 8, 10 months",
    duration: "All 3 phases, complete college blueprint",
    headline: "Phase 1 builds it. Phase 2 executes it. Phase 3 lands them on campus ready.",
    description:
      "For families who want certainty from day one. Phase 1 builds the foundation. Phase 2 executes the applications. Phase 3 prepares your student to thrive on Day 1 of college, with the E.A.S.E. Self-Advocacy Document™ built in. Founding client rate. Limited spots.",
    features: [
      "All Phase 1 deliverables (college list, resume, scholarships, benefits nav)",
      "All Phase 2 deliverables (essay coaching, appeals, fellowship app review)",
      "All Phase 3 deliverables (E.A.S.E. doc, transition coaching, first-semester plan)",
      "13 total coaching calls across all phases",
      "Monthly scholarship lists (6 months)",
      "Priority scheduling throughout",
    ],
    note: "Phases purchased separately total $9,100. Full Journey saves $600.",
    cta: "Apply for Full Journey",
    style: "accent",
    badge: "Founding client rate",
    showFounding: true,
    testId: "service-tier-3",
  },
];

// extension packages
const extensions = [
  {
    id: "phase2",
    eyebrow: "Phase 2 extension",
    name: "Application Support",
    price: "$2,500",
    cadence: "/ 3 months",
    features: [
      "Essay strategy coaching (3 essays)",
      "Writopia Lab essay workshop (via partner)",
      "Fellowship application review (3 apps)",
      "Financial aid appeal strategy (2 schools)",
      "Appeal letter writing (via partner specialist)",
      "Monthly scholarship lists (3 months)",
      "4 coaching calls",
    ],
    cta: "Add Phase 2",
    testId: "extension-phase2",
  },
  {
    id: "phase3",
    eyebrow: "Phase 3 extension",
    name: "Transition Coaching",
    price: "$1,800",
    cadence: "/ 2, 3 months",
    features: [
      "E.A.S.E. Self-Advocacy Document™ (8, 12 pages)",
      "Disability services intake preparation",
      "Professor communication scripts",
      "Mid-semester advocacy templates",
      "First-semester planning",
      "Executive function strategies",
      "3 coaching calls",
    ],
    cta: "Add Phase 3",
    testId: "extension-phase3",
  },
];

// matrix: service name + inclusion per tier (essentials, phase1, full-journey)
const serviceMatrix = [
  {
    name: "College List Building",
    sub: "15+ institutions with the Genie Dawkins HBCU Evaluation Framework™, graduation rates, MCAT prep, advising quality, founding mission, fit.",
    e: "Included",
    c: "Included",
    x: "Included",
  },
  {
    name: "Monthly Scholarship Spreadsheets",
    sub: "Custom-filtered (neurodiversity, first-gen, income, geography) with auto deadline reminders at 6 / 3 / 1 weeks.",
    e: "6 months",
    c: "3 months",
    x: "6 months",
  },
  {
    name: "Fellowship & Summer Program Matching",
    sub: "Funded opportunities, NSF REU, Smithsonian, RESESS, McNair, AmGen Scholars, SMDEP, and more.",
    e: "3 opportunities",
    c: "10+ opportunities",
    x: "10+ opportunities",
  },
  {
    name: "College Resume Building",
    sub: "Strategic narrative resumes, General, STEM, Healthcare versions translating life into achievement language.",
    e: "1 version",
    c: "2 versions",
    x: "3 versions",
  },
  {
    name: "Benefits Navigation",
    sub: "SSI/SSDI, ABLE Accounts, Voc Rehab funding, work-study impact, work-incentives planning.",
    e: false,
    c: "Included",
    x: "Included",
  },
  {
    name: "E.A.S.E. Self-Advocacy Document™",
    sub: "8, 12 page personalized document, accommodations, intake scripts, advising scripts, professor templates, key contacts.",
    e: "Add-on $1,200",
    c: "Add-on $1,200",
    x: "Included",
  },
  {
    name: "Application Planning & Strategy",
    sub: "Common App roadmap, recommendation strategy, activity list coaching, interview prep, essay feedback.",
    e: "1 essay",
    c: "3 essays",
    x: "Full coaching",
  },
  {
    name: "Transition Coaching",
    sub: "HS → college transition for neurodivergent students. Executive function, intake, advisor prep, first-semester support.",
    e: false,
    c: "6 calls",
    x: "13 calls",
  },
  {
    name: "Appeal Strategy Coaching (Light-touch)",
    sub: "1-hour strategy session, review of family-drafted appeal, financial aid phone follow-up scripts.",
    e: false,
    c: "Included",
    x: "Included",
  },
];

// ---------- FOUNDING CLIENT BANNER ----------
const FoundingBanner = ({ testId = "founding-banner" }) => (
  <p
    data-testid={testId}
    className="text-orange-600 text-[11px] md:text-xs uppercase tracking-[0.22em] font-bold leading-snug"
  >
    Founding client rate, limited to 5 families. Rate increases after founding cohort is full.
  </p>
);

// ---------- SECTIONS ----------
const Hero = () => (
  <section data-testid="services-hero" className="bg-[#FBF9F6] pt-16 md:pt-24 pb-12 md:pb-20">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
      <p className="text-orange-500 text-xs uppercase tracking-[0.28em] font-bold mb-6" data-testid="services-eyebrow">
        Services, pick your pathway
      </p>
      <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[1.02] max-w-5xl">
        Three rooms. <em className="italic">One</em> outcome: your child, accepted and <span className="bg-yellow-400 px-2">fully funded.</span>
      </h1>
      <p className="mt-8 max-w-3xl text-lg md:text-xl text-black/70 font-light leading-relaxed">
        Start with Phase 1 (3, 4 months), extend as your student needs, or commit to the Full Journey from day one. Founding client rates available for a limited number of families.
      </p>

      <div className="mt-10 max-w-3xl">
        <FoundingBanner testId="founding-banner-hero" />
      </div>
    </div>
  </section>
);

const TierCard = ({ t }) => {
  const isDark = t.style === "dark";
  const isAccent = t.style === "accent";

  const cardClass = isDark
    ? "bg-black text-[#FBF9F6] border-black"
    : isAccent
    ? "bg-orange-500 text-black border-orange-500"
    : "bg-white text-black border-black/15";

  const eyebrowColor = isDark
    ? "text-orange-400"
    : isAccent
    ? "text-black"
    : "text-orange-500";

  const headlineColor = isDark
    ? "text-yellow-400"
    : isAccent
    ? "text-black"
    : "text-black";

  const descColor = isDark
    ? "text-[#FBF9F6]/75"
    : isAccent
    ? "text-black/80"
    : "text-black/65";

  const checkBg = isDark ? "bg-orange-500" : isAccent ? "bg-black" : "bg-black";
  const checkColor = isDark ? "text-black" : isAccent ? "text-yellow-400" : "text-yellow-400";
  const featureColor = isDark ? "text-[#FBF9F6]/90" : isAccent ? "text-black/90" : "text-black/85";

  const noteColor = isDark
    ? "text-yellow-400/95"
    : isAccent
    ? "text-black/85"
    : "text-orange-700";

  const foundingLineColor = isDark
    ? "text-orange-400"
    : isAccent
    ? "text-black"
    : "text-orange-600";

  const ctaClass = isDark
    ? "bg-orange-500 text-black border-orange-500 hover:bg-yellow-400 hover:border-yellow-400"
    : isAccent
    ? "bg-black text-[#FBF9F6] border-black hover:bg-yellow-400 hover:text-black hover:border-yellow-400"
    : "bg-black text-[#FBF9F6] border-black hover:bg-orange-500 hover:border-orange-500";

  return (
    <article
      data-testid={t.testId}
      className={`relative p-8 md:p-10 border-2 ${cardClass} card-shadow-hover flex flex-col`}
    >
      {t.badge && (
        <span className="absolute -top-3 left-8 bg-yellow-400 text-black text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5 border-2 border-black">
          {t.badge}
        </span>
      )}

      <p className={`text-xs uppercase tracking-[0.28em] font-bold mb-5 ${eyebrowColor}`}>
        {t.eyebrow}
      </p>

      <h2 className="font-serif text-3xl md:text-4xl font-black tracking-tight leading-[1.05]">
        {t.name}
      </h2>

      <div className="mt-6 flex items-baseline gap-2">
        <span className="font-serif text-5xl md:text-6xl font-black tracking-tight leading-none">{t.price}</span>
        <span className={`text-base font-medium ${descColor}`}>{t.cadence}</span>
      </div>
      <p className={`mt-2 text-xs uppercase tracking-[0.18em] font-bold ${eyebrowColor}`}>{t.duration}</p>

      {t.showFounding && (
        <p
          data-testid={`${t.testId}-founding-line`}
          className={`mt-4 text-[11px] uppercase tracking-[0.2em] font-bold leading-snug ${foundingLineColor}`}
        >
          Founding client rate, limited to 5 families. Rate increases after founding cohort is full.
        </p>
      )}

      <p className={`mt-6 text-base font-semibold leading-snug ${headlineColor}`}>
        {t.headline}
      </p>

      <p className={`mt-4 text-sm font-light leading-relaxed ${descColor}`}>
        {t.description}
      </p>

      <ul className="mt-7 space-y-3 flex-1">
        {t.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className={`mt-0.5 inline-flex items-center justify-center w-5 h-5 flex-shrink-0 ${checkBg}`}>
              <Check className={`w-3 h-3 ${checkColor}`} strokeWidth={3} />
            </span>
            <span className={`text-sm leading-snug ${featureColor}`}>{f}</span>
          </li>
        ))}
      </ul>

      {t.note && (
        <p className={`mt-6 text-sm italic font-serif leading-relaxed ${noteColor}`} data-testid={`${t.testId}-note`}>
          {t.note}
        </p>
      )}

      <Link
        to="/book"
        data-testid={`${t.testId}-cta`}
        className={`mt-7 inline-flex items-center justify-center gap-2 px-6 py-4 text-sm uppercase tracking-[0.18em] font-bold border-2 transition-colors ${ctaClass}`}
      >
        {t.cta}
        <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
      </Link>
    </article>
  );
};

const Tiers = () => (
  <section data-testid="services-tiers" className="bg-[#FBF9F6] pb-16 md:pb-24">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
      <div className="grid lg:grid-cols-3 gap-6 lg:gap-7">
        {tiers.map((t) => (
          <TierCard key={t.id} t={t} />
        ))}
      </div>
      <p className="mt-8 text-sm text-black/55 italic font-serif text-center">
        All packages billed transparently. No surprise fees. Talk to Genie on a free discovery call to confirm fit before any commitment.
      </p>
    </div>
  </section>
);

// ---------- EXTENSIONS ----------
const ExtensionCard = ({ ex }) => (
  <article
    data-testid={ex.testId}
    className="relative p-8 md:p-10 border-2 border-black/15 bg-white card-shadow-hover flex flex-col"
  >
    <p className="text-xs uppercase tracking-[0.28em] font-bold mb-4 text-orange-500">{ex.eyebrow}</p>
    <h3 className="font-serif text-3xl md:text-4xl font-black tracking-tight leading-[1.05]">{ex.name}</h3>

    <div className="mt-5 flex items-baseline gap-2">
      <span className="font-serif text-5xl md:text-6xl font-black tracking-tight leading-none">{ex.price}</span>
      <span className="text-base font-medium text-black/55">{ex.cadence}</span>
    </div>

    <ul className="mt-7 space-y-3 flex-1">
      {ex.features.map((f, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 flex-shrink-0 bg-black">
            <Check className="w-3 h-3 text-yellow-400" strokeWidth={3} />
          </span>
          <span className="text-sm leading-snug text-black/85">{f}</span>
        </li>
      ))}
    </ul>

    <Link
      to="/book"
      data-testid={`${ex.testId}-cta`}
      className="mt-9 inline-flex items-center justify-center gap-2 px-6 py-4 text-sm uppercase tracking-[0.18em] font-bold border-2 bg-black text-[#FBF9F6] border-black hover:bg-orange-500 hover:border-orange-500 transition-colors"
    >
      {ex.cta}
      <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
    </Link>
  </article>
);

const Extensions = () => (
  <section data-testid="services-extensions" className="bg-[#FBF9F6] pb-24 md:pb-32 pt-4">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
      <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
        <div className="md:col-span-8">
          <p className="text-orange-500 text-xs uppercase tracking-[0.28em] font-bold mb-5">Extension packages</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.04]">
            Need more support after Phase 1? <em className="italic">Add</em> what your student needs.
          </h2>
        </div>
        <div className="md:col-span-4">
          <p className="text-base text-black/65 font-light leading-relaxed">
            Phase 1 is the foundation. These two extensions let you step into applications and transition only when you're ready, on your timeline, not a long contract.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 lg:gap-7">
        {extensions.map((ex) => (
          <ExtensionCard key={ex.id} ex={ex} />
        ))}
      </div>

      <p
        className="mt-8 text-sm text-black/60 italic font-serif text-center max-w-3xl mx-auto leading-relaxed"
        data-testid="extensions-note"
      >
        Phase 2 and Phase 3 are available to Phase 1 clients only. Most families add Phase 2 after seeing their Phase 1 results.
      </p>
    </div>
  </section>
);

// ---------- DETAILED CATALOG ----------
const Cell = ({ value }) => {
  if (value === false) {
    return (
      <span className="inline-flex items-center justify-center w-8 h-8 text-black/30" aria-label="Not included">
        <Minus className="w-4 h-4" strokeWidth={2.5} />
      </span>
    );
  }
  if (value === "Included") {
    return (
      <span className="inline-flex items-center justify-center w-8 h-8 bg-black" aria-label="Included">
        <Check className="w-4 h-4 text-yellow-400" strokeWidth={3} />
      </span>
    );
  }
  // string value
  return (
    <span className="inline-block text-xs md:text-sm font-bold uppercase tracking-wider bg-orange-500/15 text-orange-700 border border-orange-500/30 px-2.5 py-1.5 leading-tight">
      {value}
    </span>
  );
};

const TierHeader = ({ label, price, accent }) => (
  <div className={`flex flex-col items-center justify-center gap-0.5 px-4 py-5 ${accent ? "bg-orange-500 text-black" : ""}`}>
    <span className="text-xs uppercase tracking-[0.22em] font-bold">{label}</span>
    <span className={`text-[11px] uppercase tracking-[0.18em] font-bold ${accent ? "text-black/70" : "text-[#FBF9F6]/65"}`}>{price}</span>
  </div>
);

const Catalog = () => (
  <section data-testid="services-catalog" className="bg-white py-24 md:py-32 border-y-2 border-black">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
      <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
        <div className="md:col-span-7">
          <p className="text-orange-500 text-xs uppercase tracking-[0.28em] font-bold mb-5">The full services catalog</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-black leading-[1.04] tracking-tight">
            Nine services. <em className="italic">One</em> strategy desk.
          </h2>
        </div>
        <div className="md:col-span-5">
          <p className="text-black/70 text-lg font-light leading-relaxed">
            Every line below is something Genie personally builds, refines, or coaches you through. Here's exactly what's in each package, no fine print, no upsell traps.
          </p>
        </div>
      </div>

      {/* Desktop / tablet table */}
      <div className="hidden md:block border-2 border-black overflow-hidden" data-testid="services-matrix-desktop">
        <div className="grid grid-cols-[1.7fr_1fr_1fr_1fr] bg-black text-[#FBF9F6]">
          <div className="px-6 py-5 text-xs uppercase tracking-[0.22em] font-bold">Service</div>
          <TierHeader label="Essentials" price="$99/mo" />
          <TierHeader label="Phase 1" price="$4,800" accent />
          <TierHeader label="Full Journey" price="$8,500" />
        </div>
        {serviceMatrix.map((row, i) => (
          <div
            key={i}
            className={`grid grid-cols-[1.7fr_1fr_1fr_1fr] items-center ${
              i % 2 === 0 ? "bg-[#FBF9F6]" : "bg-white"
            } border-t-2 border-black/10`}
            data-testid={`matrix-row-${i}`}
          >
            <div className="px-6 py-6">
              <p className="font-serif text-lg md:text-xl font-bold text-black leading-tight">{row.name}</p>
              <p className="mt-2 text-sm text-black/60 leading-relaxed pr-4">{row.sub}</p>
            </div>
            <div className="px-4 py-6 flex items-center justify-center text-center">
              <Cell value={row.e} />
            </div>
            <div className="px-4 py-6 flex items-center justify-center text-center bg-orange-500/5 border-x-2 border-orange-500/20">
              <Cell value={row.c} />
            </div>
            <div className="px-4 py-6 flex items-center justify-center text-center">
              <Cell value={row.x} />
            </div>
          </div>
        ))}
      </div>

      {/* Mobile stack */}
      <div className="md:hidden space-y-5" data-testid="services-matrix-mobile">
        {serviceMatrix.map((row, i) => (
          <div key={i} className="border-2 border-black p-5 bg-[#FBF9F6]" data-testid={`matrix-card-${i}`}>
            <p className="font-serif text-lg font-bold text-black leading-tight">{row.name}</p>
            <p className="mt-2 text-sm text-black/60 leading-relaxed">{row.sub}</p>
            <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
              <div className="flex flex-col items-center gap-2 p-3 border border-black/15">
                <span className="uppercase tracking-wider font-bold text-black/55 text-center leading-tight">Essentials<br/><span className="text-[10px] text-black/40">$99/mo</span></span>
                <Cell value={row.e} />
              </div>
              <div className="flex flex-col items-center gap-2 p-3 border-2 border-orange-500 bg-orange-500/5">
                <span className="uppercase tracking-wider font-bold text-orange-600 text-center leading-tight">Phase 1<br/><span className="text-[10px] text-black/50">$4,800</span></span>
                <Cell value={row.c} />
              </div>
              <div className="flex flex-col items-center gap-2 p-3 border border-black/15">
                <span className="uppercase tracking-wider font-bold text-black/55 text-center leading-tight">Full Journey<br/><span className="text-[10px] text-black/40">$8,500</span></span>
                <Cell value={row.x} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- ADD-ON ----------
const AddOn = () => (
  <section data-testid="services-addon" className="bg-[#FBF9F6] py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
      <div className="grid md:grid-cols-12 gap-10 items-stretch">
        <div className="md:col-span-5">
          <p className="text-orange-500 text-xs uppercase tracking-[0.28em] font-bold mb-5">Add-on service</p>
          <h2 className="font-serif text-4xl md:text-5xl font-black tracking-tight leading-[1.05]">
            The E.A.S.E. <em className="italic">Self-Advocacy</em> Document™
          </h2>
          <p className="mt-6 text-black/70 text-lg font-light leading-relaxed">
            A personalized, modular document your student keeps for life. Not a worksheet. Not a template. A real piece of intellectual property, built around their diagnosis, their voice, and the rooms they'll need to walk into next.
          </p>

          <div className="mt-8 inline-flex items-end gap-3 bg-black text-[#FBF9F6] p-6">
            <span className="font-serif text-5xl md:text-6xl font-black leading-none text-yellow-400">$1,200</span>
            <span className="pb-1 text-xs uppercase tracking-[0.2em] font-bold text-[#FBF9F6]/65">flat, one-time</span>
          </div>
          <p className="mt-3 text-sm text-black/55">Included free with the Full Journey package. Available as an add-on for Essentials and Phase 1.</p>

          <Link
            to="/book"
            data-testid="addon-cta"
            className="mt-8 inline-flex items-center gap-2 bg-orange-500 text-black px-7 py-4 text-sm uppercase tracking-[0.18em] font-bold border-2 border-orange-500 hover:bg-black hover:text-yellow-400 hover:border-black transition-colors"
          >
            Discuss the add-on
            <Plus className="w-4 h-4" strokeWidth={3} />
          </Link>
        </div>

        <div className="md:col-span-7">
          <div className="border-2 border-black bg-white">
            <div className="bg-black text-yellow-400 px-6 py-4">
              <p className="font-serif font-bold text-lg">What's inside the document</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#FBF9F6]/70 mt-1">8, 12 pages · 8 modular sections</p>
            </div>
            <ol className="divide-y divide-black/10">
              {[
                { n: "01", t: "Who I Am as a Learner", d: "Strengths-first narrative, not a deficit list." },
                { n: "02", t: "Diagnoses & Documentation", d: "Clinical summary written in your student's voice." },
                { n: "03", t: "Accommodations I'm Requesting", d: "K, 12 IEP/504 translated into college-disability-services language." },
                { n: "04", t: "Disability Services Intake Scripts", d: "Exact phrasing to use at the first meeting." },
                { n: "05", t: "Academic Advising Scripts", d: "How to ask for what they need without over-explaining." },
                { n: "06", t: "Professor Introduction Templates", d: "Email + in-person scripts for week one." },
                { n: "07", t: "Mid-Semester Advocacy Scripts", d: "When a class isn't working and they need to course-correct." },
                { n: "08", t: "Key Contacts & Documents", d: "One-page reference, phone numbers, emails, file locations." },
              ].map((s) => (
                <li key={s.n} className="px-6 py-4 flex items-start gap-4">
                  <span className="font-serif text-2xl font-black text-orange-500 w-10 flex-shrink-0">{s.n}</span>
                  <div>
                    <p className="font-serif font-bold text-base text-black leading-tight">{s.t}</p>
                    <p className="text-sm text-black/60 mt-0.5 leading-relaxed">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ---------- COMPARE / CTA ----------
const Compare = () => (
  <section data-testid="services-compare" className="bg-black text-[#FBF9F6] py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
      <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
        <div className="md:col-span-7">
          <p className="text-orange-400 text-xs uppercase tracking-[0.28em] font-bold mb-5">Not sure where to land?</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-black leading-[1.04] tracking-tight">
            Here's the short version, <em className="italic text-yellow-400">honestly</em>.
          </h2>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        <div className="border-2 border-[#FBF9F6]/20 p-7" data-testid="compare-essentials">
          <p className="text-xs uppercase tracking-[0.22em] font-bold text-orange-400 mb-3">Pick Essentials if…</p>
          <ul className="space-y-3 text-[#FBF9F6]/85 text-sm leading-relaxed">
            <li>• Your child is a sophomore or junior and you want a head start.</li>
            <li>• You're capable, organized, and need the right intel monthly.</li>
            <li>• You want to test the relationship before going deeper.</li>
          </ul>
        </div>
        <div className="border-2 border-orange-500 bg-orange-500/10 p-7" data-testid="compare-phase1">
          <p className="text-xs uppercase tracking-[0.22em] font-bold text-yellow-400 mb-3">Pick Phase 1 if…</p>
          <ul className="space-y-3 text-[#FBF9F6]/90 text-sm leading-relaxed">
            <li>• Applications open soon and you need the foundation done right.</li>
            <li>• You're navigating IEPs, SSI/SSDI, or Voc Rehab and want a guide.</li>
            <li>• You want to start strong and add Phase 2 / 3 only if needed.</li>
          </ul>
        </div>
        <div className="border-2 border-yellow-400 bg-yellow-400/10 p-7" data-testid="compare-full-journey">
          <p className="text-xs uppercase tracking-[0.22em] font-bold text-yellow-400 mb-3">Pick Full Journey if…</p>
          <ul className="space-y-3 text-[#FBF9F6]/90 text-sm leading-relaxed">
            <li>• You want certainty from day one and the E.A.S.E. doc built in.</li>
            <li>• You want essay coaching, appeals, and transition support all the way through first semester.</li>
            <li>• You'd rather lock in $600 in savings than buy phases piecemeal.</li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const CTA = () => (
  <section data-testid="services-cta" className="bg-yellow-400 text-black py-20 md:py-28">
    <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
      <div>
        <p className="text-xs uppercase tracking-[0.28em] font-bold mb-4">Still deciding?</p>
        <h2 className="font-serif text-3xl md:text-5xl font-black tracking-tight leading-[1.05] max-w-3xl">
          Bring your questions to a 20-minute discovery call. I'll bring the receipts.
        </h2>
      </div>
      <Link
        to="/book"
        data-testid="services-bottom-cta"
        className="inline-flex items-center gap-3 bg-black text-yellow-400 px-8 py-5 text-sm uppercase tracking-[0.18em] font-bold border-2 border-black hover:bg-orange-500 hover:text-black hover:border-orange-500 transition-colors flex-shrink-0"
      >
        Book a discovery call
        <ArrowUpRight className="w-5 h-5" strokeWidth={2.5} />
      </Link>
    </div>
  </section>
);

const Services = () => {
  return (
    <div data-testid="page-services">
      <Hero />
      <Tiers />
      <Extensions />
      <Catalog />
      <AddOn />
      <Compare />
      <CTA />
    </div>
  );
};

export default Services;
